import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleAxiosError, IPaginationResponse, getAxios } from './axios';
import { ITikTokContentLog } from '@/server/models/tiktok-content-log';
import { ITikTokStatsLog } from '@/server/models/tiktok-stats-log';
import { flatten } from 'lodash';
import { ITikTokVideoLog } from '@/server/models/tiktok-video-log';
import { IInstagramContentLog } from '@/server/models/instagram-content-log';
import { IInstagramStatsLog } from '@/server/models/instagram-stats-log';
import { IInstagramVideoLog } from '@/server/models/instagram-video-log';
import { PlatformEnum } from '@/server/utils/platform-enum';
import { IRedditVideoLog } from '@/server/models/reddit-video-log';

export const useContentLogList = (platform: PlatformEnum, params: { id?: string }) => {
  const axios = getAxios();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: [platform, params],
    queryFn: ({ pageParam = 1 }) =>
      axios.get<IPaginationResponse<ITikTokContentLog | IInstagramContentLog>>(`/${platform}`, {
        params: { ...params, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const totalPages = Math.ceil(lastPage.data.pagination.total / lastPage.data.pagination.limit);
      return lastPage.data.pagination.page < totalPages
        ? lastPage.data.pagination.page + 1
        : undefined;
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    contentLogs: flatten(query.data?.pages.map(page => page.data.data)),
    pagination: query.data?.pages.findLast(Boolean)?.data.pagination,
    reset: () => {
      queryClient.setQueryData([platform, params], (data?: { pages?: []; pageParams?: [] }) => ({
        pages: data?.pages?.slice(0, 1) || [],
        pageParams: data?.pageParams?.slice(0, 1) || [],
      }));
      query.refetch();
    },
  };
};

export const useStatsLogList = (platform: PlatformEnum, params: { id?: string }) => {
  const axios = getAxios();

  const query = useQuery({
    queryKey: [`/${platform}/stats`, params],
    queryFn: () =>
      axios.get<{ data: (ITikTokStatsLog | IInstagramStatsLog)[] }>(`/${platform}/stats`, {
        params,
      }),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    throwOnError(error) {
      handleAxiosError()(error);
      return false;
    },
  });

  return {
    statsLogs: query.data?.data.data || [],
    query: query,
  };
};

export const useVideoLogList = (platform: PlatformEnum, params: { id?: string; count?: number }) => {
  const axios = getAxios();

  const query = useQuery({
    queryKey: [`/${platform}/videos`, params],
    queryFn: () =>
      axios.get<{ data: (ITikTokVideoLog | IInstagramVideoLog | IRedditVideoLog)[] }>(
        `/${platform}/videos`,
        {
          params,
        }
      ),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    throwOnError(error) {
      handleAxiosError()(error);
      return false;
    },
  });

  return {
    videoLogs: query.data?.data.data || [],
    query: query,
  };
};
